import React, { useEffect, useState } from "react";
import { Button, MessageBar, MessageBarBody, MessageBarActions } from "@fluentui/react-components";
import { AddRegular, DismissRegular } from "@fluentui/react-icons";
import { constants } from "../shared/constants/index";
import CreateMeeting from "./CreateMeeting";
import { Modal } from "../shared/modal/Modal";
import { ModalContainerWrapper } from "../shared/modal/container/ModalContainerWrapper";
import styles from "./customerMeetings.module.scss";
import MeetingsList from "./meetingsList/MeetingsList";
import { observer } from "mobx-react-lite";
import { useStore } from "../stores/Stores";
import Loader from "../shared/loader/Loader";
import UpdateMeeting from "./UpdateMeeting";
import { CustomerMeeting } from "./store/CustomerMeeting";
import { useTranslation } from "../shared/translation/Translation";
import MeetingTypeForm from "./forms/MeetingTypeForm";
import { LanguageVersion } from "../shared/types/LanguageVersion";

const CustomerMeetings: React.FC = observer(() => {
    const { meetingStore, userStore } = useStore();
    const messages = useTranslation();

    const [isCreateMeetingOpened, setIsCreateMeetingOpened] = useState(false);
    const [isUpdateMeetingOpened, setIsUpdateMeetingOpened] = useState(false);
    const [isUpdateMeetingTypeOpened, setIsUpdateMeetingTypeOpened] = useState(false);
    const [selectedMeetingToUpdate, setSelectedMeetingToUpdate] = useState<CustomerMeeting>();
    const [isUpdateMeetingSuccessAlertVisible, setIsUpdateMeetingSuccessAlertVisible] = useState(false);
    const [hasStartedLoading, setHasStartedLoading] = useState(false);

    useEffect(() => {
        setHasStartedLoading(true);
        meetingStore.loadMeetings();
        meetingStore.loadMeetingOptions(LanguageVersion[userStore.languageVersion].toLowerCase());
    }, [meetingStore]);

    const onUpdateMeetingClicked = (meeting: CustomerMeeting) => {
        setSelectedMeetingToUpdate(meeting);
        setIsUpdateMeetingOpened(true);
    };

    const onUpdateMeetingTypeClicked = (meeting: CustomerMeeting) => {
        setSelectedMeetingToUpdate(meeting);
        setIsUpdateMeetingTypeOpened(true);
    };

    const onUpdateSuccess = () => {
        setIsUpdateMeetingOpened(false);
        setIsUpdateMeetingTypeOpened(false);
        setIsUpdateMeetingSuccessAlertVisible(true);

        setInterval(() => {
            setIsUpdateMeetingSuccessAlertVisible(false);
        }, constants.updateMeetingSuccessAlertInterval);
    };

    return (
        <>
            {isCreateMeetingOpened && (
                <Modal isOpen withOverlay>
                    <ModalContainerWrapper title={messages.createMeeting.header}>
                        <CreateMeeting
                            onClose={() => {
                                setIsCreateMeetingOpened(false);
                            }}
                        />
                    </ModalContainerWrapper>
                </Modal>
            )}
            {isUpdateMeetingOpened && (
                <Modal isOpen withOverlay>
                    <ModalContainerWrapper title={messages.updateMeeting.header}>
                        <UpdateMeeting
                            meeting={selectedMeetingToUpdate!}
                            onClose={() => {
                                setIsUpdateMeetingOpened(false);
                            }}
                            onSuccess={onUpdateSuccess}
                        />
                    </ModalContainerWrapper>
                </Modal>
            )}
            {isUpdateMeetingTypeOpened && (
                <Modal isOpen withOverlay>
                    <ModalContainerWrapper title={messages.updateMeeting.header}>
                        <MeetingTypeForm
                            meeting={selectedMeetingToUpdate!}
                            onClose={() => {
                                setIsUpdateMeetingTypeOpened(false);
                            }}
                            onSuccess={onUpdateSuccess}
                        />
                    </ModalContainerWrapper>
                </Modal>
            )}
            <div className={styles.wrapper}>
                {hasStartedLoading && !meetingStore.loadMeetingsRequestStatus.isPending && (
                    <div
                        className={`${styles.newMeetingButton}${
                            !meetingStore.loadMeetingsRequestStatus.isDone || meetingStore.meetings.length > 0
                                ? ` ${styles.alignEnd}`
                                : ""
                        }`}
                    >
                        <Button
                            icon={<AddRegular />}
                            iconPosition="before"
                            appearance="primary"
                            onClick={() => {
                                setIsCreateMeetingOpened(true);
                            }}
                        >
                            {messages.common.newMeeting}
                        </Button>
                    </div>
                )}
                {isUpdateMeetingSuccessAlertVisible && (
                    <MessageBar className={styles.alert} intent="success">
                        <MessageBarBody>{messages.meetingsList.updateMeetingSuccessAlert}</MessageBarBody>
                        <MessageBarActions
                            containerAction={
                                <Button
                                    aria-label="dismiss"
                                    appearance="transparent"
                                    icon={<DismissRegular />}
                                    onClick={() => setIsUpdateMeetingSuccessAlertVisible(false)}
                                />
                            }
                        />
                    </MessageBar>
                )}
                {!hasStartedLoading || meetingStore.loadMeetingsRequestStatus.isPending ? (
                    <Loader text={messages.meetingsList.loading} />
                ) : (
                    meetingStore.loadMeetingsRequestStatus.isDone && (
                        <MeetingsList
                            customerMeetings={meetingStore.meetings}
                            onUpdateMeetingClicked={onUpdateMeetingClicked}
                            onUpdateMeetingTypeClicked={onUpdateMeetingTypeClicked}
                        />
                    )
                )}
            </div>
        </>
    );
});

export default CustomerMeetings;
